import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import * as Sentry from '@sentry/capacitor';
import { get } from 'lodash';
import { catchError, map, mergeMap, of, startWith, switchMap } from 'rxjs';
import { Logger } from '@app/utils/logger';
import { AuthActions } from '@components/auth/auth.actions';
import { QuestionnaireGeneralActions } from './general.actions';
import { selectQuestionnaireGeneralFormForSubmitV3 } from './general.selectors';
import { QuestionnaireGeneralService } from './general.service';

/**
 * @description Questionnaire General Effects.
 * @exports
 * @class QuestionnaireGeneralEffects
 */
@Injectable()
export class QuestionnaireGeneralEffects extends QuestionnaireGeneralService {
  private readonly actions = inject(Actions);
  private readonly store = inject(Store);
  private readonly logger = Logger.getInstance(
    QuestionnaireGeneralEffects.name
  );

  readonly onAuthLogout_SwitchProfile = createEffect(
    () => {
      return this.actions.pipe(
        ofType(AuthActions.logout, AuthActions.switchProfile),
        map(() => QuestionnaireGeneralActions.reset())
      );
    },
    { dispatch: true }
  );

  readonly onFetchMasterDisease = createEffect(
    () => {
      return this.actions.pipe(
        ofType(QuestionnaireGeneralActions.fetchMasterDisease),
        switchMap((props) =>
          this.fetchMasterDisease(props).pipe(
            mergeMap((response) => [
              QuestionnaireGeneralActions.setMasterDisease(response),
              QuestionnaireGeneralActions.setLoading({
                key: 'fetchMasterDisease',
                value: false
              })
            ]),
            startWith(
              QuestionnaireGeneralActions.setLoading({
                key: 'fetchMasterDisease',
                value: true
              })
            )
          )
        )
      );
    },
    { dispatch: true }
  );

  readonly onFetchMasterMedication = createEffect(
    () => {
      return this.actions.pipe(
        ofType(QuestionnaireGeneralActions.fetchMasterMedication),
        switchMap((props) =>
          this.fetchMasterMedication(props).pipe(
            mergeMap((response) => [
              QuestionnaireGeneralActions.setMasterMedication(response),
              QuestionnaireGeneralActions.setLoading({
                key: 'fetchMasterMedication',
                value: false
              })
            ]),
            startWith(
              QuestionnaireGeneralActions.setLoading({
                key: 'fetchMasterMedication',
                value: true
              })
            )
          )
        )
      );
    },
    { dispatch: true }
  );

  readonly onSubmit = createEffect(
    () => {
      return this.actions.pipe(
        ofType(QuestionnaireGeneralActions.submit),
        concatLatestFrom(() => [
          this.store.select(selectQuestionnaireGeneralFormForSubmitV3)
        ]),
        switchMap(([, form]) =>
          this.submitOnboardingQuestionnaireV3({ form }).pipe(
            mergeMap((response) => [
              QuestionnaireGeneralActions.setLoading({
                key: 'submit',
                value: false
              }),
              QuestionnaireGeneralActions.setSubmissionResponse({
                success: !!get(response, 'id', null),
                message: 'Submission successful',
                response
              })
            ]),
            catchError((error) => {
              this.logger.error(
                '[QuestionnaireGeneralEffects] onSubmit Error:',
                error
              );
              Sentry.withScope((scope) => {
                scope.setTransactionName(
                  '[QuestionnaireGeneralEffects] onSubmit Error:'
                );
                Sentry.captureException(error, scope);
              });
              return of(
                QuestionnaireGeneralActions.setLoading({
                  key: 'submit',
                  value: false
                }),
                QuestionnaireGeneralActions.setSubmissionResponse({
                  success: false,
                  message: 'Submission failed',
                  response: null
                })
              );
            }),
            startWith(
              QuestionnaireGeneralActions.setLoading({
                key: 'submit',
                value: true
              })
            )
          )
        )
      );
    },
    { dispatch: true }
  );
}
